import LanguageSelect from '@/components/Form/LanguageSelect'
import BenefitsOffSvg from '@/components/SvgComponents/BenefitsOffSvg'
import BenefitsOnSvg from '@/components/SvgComponents/BenefitsOnSvg'
import HomeOffSvg from '@/components/SvgComponents/HomeOffSvg'
import HomeOnSvg from '@/components/SvgComponents/HomeOnSvg'
import RuleOffSvg from '@/components/SvgComponents/RuleOffSvg'
import RuleOnSvg from '@/components/SvgComponents/RuleOnSvg'
import SpotOffSvg from '@/components/SvgComponents/SpotOffSvg'
import SpotOnSvg from '@/components/SvgComponents/SpotOnSvg'
import { raundUrl } from '@/lib/custom'
import { usePresentCount } from '@/lib/presentsCheck'
import { useCampaignSlug } from '@/lib/useCampaignSlug'
import { useFetchGet } from '@/lib/useFetch'
import { useI18nUrls } from '@/lib/useI18nUrls'
import { useIsIOS } from '@/lib/useIsIos'
import { useIsPWA } from '@/lib/useIsPwa'
import { useLocalStorageClear } from '@/lib/useLocalStorageClear'
import type { RootState } from '@/store/store'
import styles from '@/styles/Menu.module.scss'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useState, useRef, useEffect, Fragment, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useSWR from 'swr'

const MenuItem = ({
  type,
  name,
  idx,
  url,
}: {
  type: number
  name: string
  url: string
  idx: number
}) => {
  return (
    <>
      {(() => {
        if (type === 1) {
          return (
            <button
              type="button"
              onClick={() => {
                location.href = url
              }}
              className={`block text-h1 font-bold text-sub ${1 <= idx ? 'mt-5' : ''}`}
            >
              {name}
            </button>
          )
        }
        if (type === 2) {
          return (
            <a
              href={`${url}`}
              className={`block text-h1 font-bold text-sub ${1 <= idx ? 'mt-5' : ''}`}
              target="_blank"
              rel="noreferrer"
            >
              {name}
            </a>
          )
        }
      })()}
    </>
  )
}

export const Menu = memo(() => {
  const { t } = useTranslation()
  const { isIOS } = useIsIOS()
  const { isPWA } = useIsPWA()
  const router = useRouter()
  const [menuState, setMenuState] = useState(true)
  const [menuToggleState, setMenuToggleState] = useState(true)
  const { termsUrl, faqUrl, privacyPolicyUrl } = useI18nUrls()
  const menuElem = useRef(null)
  const { pathname } = router
  const currentPath = pathname.split('/').filter(Boolean)
  const { gen } = useCampaignSlug()
  const clear = useLocalStorageClear()
  const { getWithAuth: getMenu } = useFetchGet<Api.menusResBody>({})
  const { data: menu } = useSWR<Api.menusResBody>(raundUrl.menu, getMenu)

  const newRewardsCountSelector = useSelector((state: RootState) => state.raund.newRewardsCount)
  const { getWithoutAuth: campaignGet } = useFetchGet<{ campaign: Api.CampaignResBody }>({})
  const { data: campaignData } = useSWR<{ campaign: Api.CampaignResBody }>(raundUrl.campaign, campaignGet)

  const { setPresentCount } = usePresentCount()

  useEffect(() => {
    const fetchPresents = async () => {
      await setPresentCount()
    }
    fetchPresents()
  }, [])

  const LogOut = () => {
    clear()
    location.href = gen('/auth/signin')
  }

  useEffect(() => {
    if (!menuState) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [menuState])

  const handleMenuOpen = (e) => {
    const { currentTarget } = e
    if (menuToggleState && currentTarget.classList.contains('is_close')) {
      setMenuState(false)
      currentTarget.classList.remove('is_close')
      setMenuToggleState(false)
      return
    }

    setMenuState(true)
    currentTarget.classList.add('is_close')
    setMenuToggleState(true)
    return
  }
  const paddingX = isIOS && isPWA ? 'px-6' : undefined

  return (
    <nav>
      <div className={`${styles.c_menu_body}`} ref={menuElem} aria-hidden={menuState} id="c_menu">
        <div className={styles.c_menu_contents}>
          {menu?.menus?.map((v: { url: string; type: number; name: string }, i) => {
            return (
              <Fragment key={`menu-item-${v.url}-${i}`}>
                {v.url.startsWith('/logout') ? (
                  <div
                    className="mt-5 block text-h1 font-bold text-sub"
                    onClick={() => LogOut()}
                    onKeyDown={(e) => e.key === 'Enter' && LogOut()}
                    role="button"
                    tabIndex={0}
                  >
                    {t('common.logout', 'ログアウト')}
                  </div>
                ) : v.url.startsWith('/reset') ? (
                  // resetの場合も固定でMenuのアイテムを返す
                  <MenuItem
                    type={v.type}
                    name={t('common.reset', '獲得スタンプをリセットする')}
                    url={gen('/reset')}
                    idx={i}
                  />
                ) : (
                  // そのほかの場合のみAPIのレスポンスをそのまま利用
                  <MenuItem type={v.type} name={v.name} url={v.url} idx={i} />
                )}
              </Fragment>
            )
          })}
          <a href={faqUrl} className="mt-5 block text-h1 font-bold text-sub" target="_blank" rel="noreferrer noopener">
            {t('common.faq', 'FAQ')}
          </a>
          <a
            href={termsUrl}
            className="mt-5 block text-h1 font-bold text-sub"
            target="_blank"
            rel="noreferrer noopener"
          >
            {t('common.terms', '利用規約')}
          </a>
          <a
            href={privacyPolicyUrl}
            className="mt-5 block text-h1 font-bold text-sub"
            target="_blank"
            rel="noreferrer noopener"
          >
            {t('common.privacy_policy', 'プライバシーポリシー')}
          </a>
          <div className="text-sub">
            {campaignData?.campaign?.isLocalize && campaignData?.campaign.languages.length > 1 && (
              <LanguageSelect languages={campaignData?.campaign.languages} />
            )}
          </div>
          <Link
            href={{
              pathname: gen('/restore'),
            }}
            legacyBehavior={true}
          >
            <a className="mt-5 block text-h1 font-bold text-sub">{t('common.restore', 'データ復元')}</a>
          </Link>
          {campaignData?.campaign.custom === 'NEXTTUBE' && (
            <Link
              href={{
                pathname: gen('/ocrForm'),
              }}
              legacyBehavior={true}
            >
              <a className="mt-5 block text-h1 font-bold text-sub">{t('common.form', 'お問い合わせ')}</a>
            </Link>
          )}
          {menu ? (
            <div className="fixed bottom-[90px] right-[25px] text-[12px] text-sub">ID: {menu.user_id}</div>
          ) : null}
        </div>
      </div>
      <div className={`item-center flex items-center justify-between ${styles.c_menu} ${paddingX}`}>
        <div className={`text-center ${styles.c_menu_button}`}>
          <Link
            href={{
              pathname: gen('/'),
            }}
            legacyBehavior={true}
          >
            <a className={`block ${currentPath[1] === undefined ? '' : 'text-[#8c8c8c]'}`}>
              <span className={`${styles.c_menu_icon} block`}>
                {currentPath[1] === undefined ? <HomeOnSvg /> : <HomeOffSvg />}
              </span>
              <p className="text-xs">{t('common.home')}</p>
            </a>
          </Link>
        </div>
        <div className={`text-center ${styles.c_menu_button}`}>
          <Link
            href={{
              pathname: gen('/spot/'),
            }}
            legacyBehavior={true}
          >
            <a className={`block ${currentPath[1] === 'spot' ? '' : 'text-[#8c8c8c]'}`} aria-label="spotlink">
              <span className={`${styles.c_menu_icon} block`}>
                {currentPath[1] === 'spot' ? <SpotOnSvg /> : <SpotOffSvg />}
              </span>
              <p className="text-xs">{t('common.spot')}</p>
            </a>
          </Link>
        </div>
        <div className={`text-center ${styles.c_menu_button}`}>
          <Link
            href={{
              pathname: gen('/rewards'),
            }}
            legacyBehavior={true}
          >
            <a
              className={`block ${currentPath[1] === 'rewards' ? '' : 'text-[#8c8c8c]'} ${
                newRewardsCountSelector !== 0 ? styles.c_batch : ''
              }`}
            >
              <span className={`${styles.c_menu_icon} block`}>
                {currentPath[1] === 'rewards' ? <BenefitsOnSvg /> : <BenefitsOffSvg />}
              </span>
              <p className="text-xs">{t('common.goods', '特典情報')}</p>
            </a>
          </Link>
        </div>
        <div className={`text-center ${styles.c_menu_button}`}>
          <Link
            href={{
              pathname: gen('/rule'),
            }}
            legacyBehavior={true}
          >
            <a className={`block ${currentPath[1] === 'rule' ? '' : 'text-[#8c8c8c]'}`}>
              <span className={`${styles.c_menu_icon} block`}>
                {currentPath[1] === 'rule' ? <RuleOnSvg /> : <RuleOffSvg />}
              </span>
              <p className="text-xs">{t('common.play')}</p>
            </a>
          </Link>
        </div>
        <div className={`text-center ${styles.c_menu_button}`}>
          <button type="button" className="is_close" onClick={(e) => handleMenuOpen(e)}>
            <span className={`flex flex-col justify-center ${styles.c_menu_icon}`}>
              <img
                src={`${process.env.NEXT_PUBLIC_SITEURL}/images/img_menu_${menuState ? 'off' : 'on'}.svg`}
                alt=""
                className="m-auto"
              />
            </span>
            <span className="text-xs text-[#8c8c8c]">{t('common.menu')}</span>
          </button>
        </div>
      </div>
    </nav>
  )
})
